import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { forkJoin, Observable, firstValueFrom, Subscription } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import {
  CommentsRepositoryService,
  ReportTemplateRepositoryService,
  EmailTemplateRepositoryService,
  CommentType,
  PracticeReportTemplate,
  EditorReportTemplate,
  EditorSystemReportTemplate,
  SystemReportTemplate,
  PracticeCommentTemplate,
  CreateCommentTemplate,
  EmailTemplate,
  StateStore, StateStorePromises
} from '@xmedical/core';
import { CacheService } from '../services/cache.service';
import {CoreEvent, CoreEventsService} from '../services/core-events.service';
import { PracticeStore } from './practice.store';
import {AuthenticationService} from '../auth/authentication.service';

/**
 * TemplatesStore
 *
 * Manages practice-specific templates used throughout the application.
 * Uses Angular signals for reactive state management.
 *
 * Data managed:
 * - Comment templates (pre-defined comment texts)
 * - Comment types (categories for comments)
 * - Report templates (document templates)
 * - Email templates (email message templates)
 *
 * All data is cached in localStorage with user ID prefix.
 * Data is reloaded when practice changes.
 * Each store registers its own boot steps for initialization tracking.
 */
@Injectable({
  providedIn: 'root'
})
export class TemplatesStore implements StateStore, StateStorePromises, OnDestroy {
  private commentsRepo = inject(CommentsRepositoryService);
  private reportTemplatesRepo = inject(ReportTemplateRepositoryService);
  private emailTemplatesRepo = inject(EmailTemplateRepositoryService);
  private cache = inject(CacheService);
  private events = inject(CoreEventsService);
  private auth = inject(AuthenticationService);
  private practiceStore = inject(PracticeStore);

  private eventSubscription: Subscription;

  constructor() {
    // Listen for practice changes and reload data
    this.eventSubscription = this.events.events$.subscribe(event => {
      if (event.type === 'practice_change') {
        this.reloadForPracticeSwitch().subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
  }

  // State signals
  readonly commentTypes = signal<CommentType[]>([]);
  readonly commentTemplates = signal<PracticeCommentTemplate[]>([]);
  readonly reportTemplates = signal<PracticeReportTemplate[]>([]);
  readonly systemReportTemplates = signal<SystemReportTemplate[]>([]);
  readonly emailTemplates = signal<EmailTemplate[]>([]);
  readonly loaded = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  // Promise for tracking in-flight load operation
  loadingPromise: Promise<void> | null = null;

  // Computed signals
  readonly isReady = computed(() => this.loaded());

  /**
   * Initialize all templates
   * Loads all template types in parallel
   *
   * @returns Observable that completes when all data is loaded
   */
  initialize(): Observable<void> {
    this.loading.set(true);

    // Load all data in parallel
    return forkJoin({
      commentTypes: this.loadCommentTypes(),
      commentTemplates: this.loadCommentTemplates(),
      reportTemplates: this.loadReportTemplates(),
      emailTemplates: this.loadEmailTemplates()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Reload templates for practice switch
   * Clears cache and reloads fresh data for the new practice context
   *
   * @returns Observable that completes when all data is reloaded
   */
  reloadForPracticeSwitch(): Observable<void> {
    this.loading.set(true);
    this.loaded.set(false);

    // Clear cached data
    this.clearTemplates();

    // Reload all data
    return forkJoin({
      commentTypes: this.loadCommentTypes(),
      commentTemplates: this.loadCommentTemplates(),
      reportTemplates: this.loadReportTemplates(),
      emailTemplates: this.loadEmailTemplates()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Load comment types
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadCommentTypes(): Observable<CommentType[]> {
    return this.cache.cacheFirst(
      'comment_types',
      () => this.commentsRepo.loadPatientCommentTypes(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (types) => {
          this.commentTypes.set(types);
          this.events.trigger(new CoreEvent('comment_types_loaded'));

        },
        onFetchComplete: (types) => {
          this.commentTypes.set(types);
          this.events.trigger(new CoreEvent('comment_types_loaded'));

        }
      }
    );
  }

  /**
   * Load comment templates
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadCommentTemplates(): Observable<PracticeCommentTemplate[]> {
    return this.cache.cacheFirst(
      'comment_templates',
      () => this.commentsRepo.loadPatientCommentTemplates(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (templates) => {
          this.commentTemplates.set(templates);
          this.events.trigger(new CoreEvent('comment_templates_loaded'));

        },
        onFetchComplete: (templates) => {
          this.commentTemplates.set(templates);
          this.events.trigger(new CoreEvent('comment_templates_loaded'));

        }
      }
    );
  }

  /**
   * Load report templates
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadReportTemplates(): Observable<PracticeReportTemplate[]> {
    return this.cache.cacheFirst(
      'report_templates',
      () => this.reportTemplatesRepo.getReportTemplates(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (templates) => {
          this.reportTemplates.set(templates);
          this.events.trigger(new CoreEvent('report_templates_loaded'));

        },
        onFetchComplete: (templates) => {
          this.reportTemplates.set(templates);
          this.events.trigger(new CoreEvent('report_templates_loaded'));

        }
      }
    );
  }

  /**
   * Load email templates
   * Uses cache-first strategy with no TTL, user-scoped cache key
   */
  private loadEmailTemplates(): Observable<EmailTemplate[]> {
    return this.cache.cacheFirst(
      'email_templates',
      () => this.emailTemplatesRepo.getEmailTemplates(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (templates) => {
          this.emailTemplates.set(templates);
          this.events.trigger(new CoreEvent('email_templates_loaded'));

        },
        onFetchComplete: (templates) => {
          this.emailTemplates.set(templates);
          this.events.trigger(new CoreEvent('email_templates_loaded'));

        }
      }
    );
  }

  /**
   * Ensure templates are loaded - on-demand loading for page refresh scenarios.
   * - If already loaded, returns immediately
   * - If loading in progress, waits for completion
   * - If not loaded, triggers fetch and waits
   *
   * @returns Promise that resolves when templates are available
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded()) return;

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Not loaded and not loading - trigger initialization
    this.loadingPromise = firstValueFrom(this.initialize()).finally(() => {
      this.loadingPromise = null;
    });

    return this.loadingPromise;
  }

  /**
   * Update comment template
   *
   * @param template - Updated template
   * @returns Observable that completes when template is updated
   */
  updateCommentTemplate(template: PracticeCommentTemplate): Observable<boolean> {
    return this.commentsRepo.updateTemplate(template).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the template in the array
          const templates = this.commentTemplates();
          const index = templates.findIndex(t => t.id === template.id);
          if (index > -1) {
            const newTemplates = [...templates];
            newTemplates[index] = template;
            this.commentTemplates.set(newTemplates);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-comment_templates`, newTemplates);
            this.events.trigger(new CoreEvent('comment_templates_loaded'));
          }
        }
      })
    );
  }

  /**
   * Create comment template
   *
   * @param template - New template to create
   * @returns Observable that completes when template is created, returns the new template ID
   */
  createCommentTemplate(template: CreateCommentTemplate): Observable<number> {
    return this.commentsRepo.createTemplate(template).pipe(
      tap(() => {
        // Reload templates to get full entity with server-generated fields
        this.loadCommentTemplates().subscribe();
      })
    );
  }

  /**
   * Delete comment template
   *
   * @param templateId - ID of template to delete
   * @returns Observable that completes when template is deleted
   */
  deleteCommentTemplate(templateId: number): Observable<boolean> {
    return this.commentsRepo.deleteTemplate(templateId).pipe(
      tap(success => {
        if (success) {
          const templates = this.commentTemplates();
          const newTemplates = templates.filter(t => t.id !== templateId);
          this.commentTemplates.set(newTemplates);

          // Update cache
          const userId = this.auth.getUserId();
          this.cache.set(`${userId}-comment_templates`, newTemplates);
          this.events.trigger(new CoreEvent('comment_templates_loaded'));
        }
      })
    );
  }

  /**
   * Create email template
   *
   * @param template - New template to create
   * @returns Observable that completes when template is created, returns the new template ID
   */
  createEmailTemplate(template: EmailTemplate): Observable<number> {
    return this.emailTemplatesRepo.createEmailTemplate(template).pipe(
      tap(id => {
        // Add the new template to the signal
        template.id = id;
        const templates = this.emailTemplates();
        const newTemplates = [...templates, template];
        this.emailTemplates.set(newTemplates);

        // Update cache
        const userId = this.auth.getUserId();
        this.cache.set(`${userId}-email_templates`, newTemplates);
        this.events.trigger(new CoreEvent('email_templates_loaded'));
      })
    );
  }

  /**
   * Update email template
   *
   * @param template - Updated template
   * @returns Observable that completes when template is updated
   */
  updateEmailTemplate(template: EmailTemplate): Observable<boolean> {
    return this.emailTemplatesRepo.updateEmailTemplate(template).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the template in the array
          const templates = this.emailTemplates();
          const index = templates.findIndex(t => t.id === template.id);
          if (index > -1) {
            const newTemplates = [...templates];
            newTemplates[index] = template;
            this.emailTemplates.set(newTemplates);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-email_templates`, newTemplates);
            this.events.trigger(new CoreEvent('email_templates_loaded'));
          }
        }
      })
    );
  }

  /**
   * Create report template
   *
   * @param template - New template to create (EditorReportTemplate)
   * @returns Observable that completes when template is created, returns the new template ID
   */
  createReportTemplate(template: EditorReportTemplate): Observable<number> {
    return this.reportTemplatesRepo.createReportTemplate(template).pipe(
      tap(id => {
        // Create PracticeReportTemplate wrapper for the store
        const practiceTemplate: PracticeReportTemplate = {
          id: id,
          practiceId: this.practiceStore.selectedPractice()?.id ?? 0,
          template: template
        };

        const templates = this.reportTemplates();
        const newTemplates = [...templates, practiceTemplate];
        this.reportTemplates.set(newTemplates);

        // Update cache
        const userId = this.auth.getUserId();
        this.cache.set(`${userId}-report_templates`, newTemplates);
        this.events.trigger(new CoreEvent('report_templates_loaded'));
      })
    );
  }

  /**
   * Update report template
   *
   * @param id - Template ID
   * @param template - Updated template (EditorReportTemplate)
   * @returns Observable that completes when template is updated
   */
  updateReportTemplate(id: number, template: EditorReportTemplate): Observable<boolean> {
    // Add ID to template for repository call
    template.id = id;

    return this.reportTemplatesRepo.updateReportTemplate(template).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the template in the array
          const templates = this.reportTemplates();
          const index = templates.findIndex(t => t.id === id);
          if (index > -1) {
            const newTemplates = [...templates];
            // Update the nested template while preserving the wrapper
            newTemplates[index] = {
              ...newTemplates[index],
              template: template
            };
            this.reportTemplates.set(newTemplates);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-report_templates`, newTemplates);
            this.events.trigger(new CoreEvent('report_templates_loaded'));
          }
        }
      })
    );
  }

  /**
   * Load a single report template by ID
   *
   * @param id - Template ID
   * @returns Observable with the template
   */
  loadReportTemplate(id: number): Observable<PracticeReportTemplate> {
    return this.reportTemplatesRepo.loadReportTemplate(id);
  }

  /**
   * Load system report templates for editing
   * Used by report builder - loads on demand, not during app boot
   *
   * @returns Observable with the templates
   */
  loadSystemReportTemplates(): Observable<SystemReportTemplate[]> {
    return this.reportTemplatesRepo.getSystemReportTemplatesForEdit().pipe(
      tap(templates => {
        this.systemReportTemplates.set(templates);
        this.events.trigger(new CoreEvent('system_report_templates_loaded'));
      })
    );
  }

  /**
   * Load a single system report template by ID
   *
   * @param id - Template ID
   * @returns Observable with the template
   */
  loadSystemReportTemplate(id: number): Observable<SystemReportTemplate> {
    return this.reportTemplatesRepo.loadSystemReportTemplate(id);
  }

  /**
   * Create system report template
   *
   * @param template - New template to create (EditorReportTemplate or EditorSystemReportTemplate)
   * @returns Observable that completes when template is created, returns the new template ID
   */
  createSystemReportTemplate(template: EditorReportTemplate | EditorSystemReportTemplate): Observable<number> {
    return this.reportTemplatesRepo.createSystemReportTemplate(template).pipe(
      tap(id => {
        // Reload all system templates from server after create
        this.loadSystemReportTemplates().subscribe();
      })
    );
  }

  /**
   * Update system report template
   *
   * @param template - Updated template (EditorReportTemplate or EditorSystemReportTemplate)
   * @returns Observable that completes when template is updated
   */
  updateSystemReportTemplate(template: EditorReportTemplate | EditorSystemReportTemplate): Observable<boolean> {
    return this.reportTemplatesRepo.updateSystemReportTemplate(template).pipe(
      tap(success => {
        if (success) {
          // Update the template in the signal
          const templates = this.systemReportTemplates();
          const index = templates.findIndex(t => t.id === template.id);
          if (index > -1) {
            const newTemplates = [...templates];
            // Update the nested template while preserving the wrapper
            newTemplates[index] = {
              ...newTemplates[index],
              template: template as EditorSystemReportTemplate
            };
            this.systemReportTemplates.set(newTemplates);
            this.events.trigger(new CoreEvent('system_report_templates_loaded'));
          }
        }
      })
    );
  }

  /**
   * Clear templates data (called on practice switch or logout)
   */
  clearTemplates(): void {
    this.commentTypes.set([]);
    this.commentTemplates.set([]);
    this.reportTemplates.set([]);
    this.systemReportTemplates.set([]);
    this.emailTemplates.set([]);
    this.loaded.set(false);

    // Clear cache
    const userId = this.auth.getUserId();
    this.cache.invalidate('comment_types', userId);
    this.cache.invalidate('comment_templates', userId);
    this.cache.invalidate('report_templates', userId);
    this.cache.invalidate('email_templates', userId);
  }
}
